import { useState } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import QuillDelta from 'quill-delta';
import { parseInt } from 'lodash';

import {
	Button,
	Card,
	Heading,
	Subheading,
	Label,
	Input,
	NumericInput,
	Skeleton,
	Tags,
	Toolbar,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

export default Skeleton(
	function EditSubgoalCard(props: {
		subgoalID: string;
		isNew?: boolean;
		type?: graphql.GoalChecklistItemType;
		onDismiss: (success: boolean) => void;
	}) {
		const { localize, language } = useLocalize();
		const { t } = useTranslation();

		const [{ value }, , { setValue }] = useField<graphql.GoalChecklistItem>(
			`subgoals.${props.subgoalID}`,
		);

		const [dueDate, setDueDate] = useState<Date | undefined>(value?.dueDate);
		const [title, setTitle] = useState(localize(value?.title ?? [])?.text ?? '');
		const [progressValue, setProgressValue] = useState(value?.progress.value?.toString() ?? '0');
		const [upper, setUpper] = useState(value?.progress.upper?.toString());

		const type = value?.progress.type ?? props.type;

		return (
			<Card>
				<Flex alignItems="center">
					<Box flexGrow={0} flexShrink={0} width={24} mr={2} />
					<Box flexGrow={1} flexShrink={1}>
						{props.type && props.isNew && (
							<Box mb={3}>
								<Subheading>
									{t(`goals.subgoals.create.heading.${props.type.toLowerCase()}`)}
								</Subheading>
							</Box>
						)}
						<Box mb={2}>
							<Input autoFocus width="100%" value={title} onChange={setTitle} />
						</Box>
						{(type === graphql.GoalChecklistItemType.Currency ||
							type === graphql.GoalChecklistItemType.Range) && (
							<Flex mb={3}>
								<Box width={3 / 12} mr={0}>
									<Label mb={2}>
										{props.isNew
											? t('goals.subgoals.edit.lower.label')
											: t('goals.subgoals.edit.value.label')}
									</Label>

									<Flex>
										<NumericInput
											prefix={type === graphql.GoalChecklistItemType.Currency ? '€' : undefined}
											value={progressValue ?? '0'}
											// eslint-disable-next-line @typescript-eslint/ban-ts-comment
											// @ts-ignore
											onChange={setProgressValue}
											mergeWith="right"
										/>
									</Flex>
								</Box>
								<Box width={3 / 12}>
									<Label mb={2}>{t('goals.subgoals.edit.upper.label')} </Label>
									<Flex alignItems="center">
										<NumericInput
											prefix={type === graphql.GoalChecklistItemType.Currency ? '€' : undefined}
											value={upper ?? '0'}
											onChange={setUpper}
											mergeWith="left"
										/>
									</Flex>
								</Box>
							</Flex>
						)}
						<Toolbar mt={3} alignItems="center" compact>
							<Tags.Item.DatePicker
								label={t('goals.subgoals.dueDate.label')}
								value={dueDate}
								onChange={(newDueDate) => {
									setDueDate(newDueDate);
								}}
							/>
							<Toolbar.Spacing />
							<Button
								type="secondary"
								title={t('goals.subgoals.cancel.button')}
								onClick={() => props.onDismiss(false)}
							/>
							<Button
								type="primary"
								title={t('goals.subgoals.save.button')}
								onClick={() => {
									const combinedValue = progressValue
										? parseInt(progressValue)
										: value?.progress.value;

									const progress =
										type === graphql.GoalChecklistItemType.Checkbox
											? {
													type: value?.progress.type ?? props.type,
													value: combinedValue,
													lower: undefined,
													upper: undefined,
											  }
											: {
													type: value?.progress.type ?? props.type,
													value: combinedValue,
													lower: props.isNew ? combinedValue : value?.progress.lower,
													upper: upper ? parseInt(upper) : 1,
											  };

									setValue({
										...value,
										title: [{ languageID: language.id, text: title }],
										description: [{ languageID: language.id, text: new QuillDelta() }],
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										progress: progress as any,
										dueDate,
									});
									props.onDismiss(true);
								}}
							/>
						</Toolbar>
					</Box>
				</Flex>
			</Card>
		);
	},
	function EditSubgoalCardSkeleton(props: BoxProps) {
		return (
			<Card.Skeleton {...props}>
				<Heading.Skeleton />
			</Card.Skeleton>
		);
	},
);
